import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { signOut } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogOut, Search, UserCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { NotificationBell } from '@/components/notifications';
import { LanguageSelector } from '@/components/ui/language-selector';
import { RoleSwitcher } from '@/components/layout/RoleSwitcher';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

export const DashboardHeader = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { language } = useLanguage();

  const handleLogout = async () => {
    try {
      await signOut(auth);
      toast({
        title: "Logged out",
        description: "You've been successfully logged out.",
      });
      navigate('/login');
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to log out. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getProfilePath = () => {
    const path = location.pathname;
    if (path.startsWith('/buyer')) return '/buyer/profile';
    if (path.startsWith('/seller')) return '/seller/profile';
    if (path.startsWith('/consultant')) return '/consultant/profile';
    if (path.startsWith('/dashboard/broker')) return '/dashboard/broker/profile';
    if (path.startsWith('/lender') || path.startsWith('/dashboard/lender')) return '/lender/profile';
    if (path.startsWith('/partners') || path.startsWith('/internal-partners')) return '/partners/profile';
    return '/profile';
  };

  return (
    <header className="h-16 border-b bg-card px-6 flex items-center justify-between">
      <div className="flex items-center gap-4 flex-1 max-w-xl">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search deals, documents, partners..."
            className="pl-10"
          />
        </div>
      </div>
      
      <div className="flex items-center gap-3">
        <Link to={getProfilePath()}>
          <Button variant="ghost" size="sm" className="gap-2">
            <UserCircle className="h-4 w-4" />
            <span className="hidden md:inline">{language === 'fr' ? 'Profil' : 'Profile'}</span>
          </Button>
        </Link>
        <RoleSwitcher />
        <LanguageSelector />
        <NotificationBell />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="gap-2">
              <Avatar className="h-8 w-8">
                <AvatarImage src={user?.photoURL || undefined} alt={user?.displayName || user?.email || 'Profile'} />
                <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
                  {user?.email?.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm max-w-[150px] truncate hidden md:inline">
                {user?.email}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/settings')}>
              Settings
            </DropdownMenuItem>
<DropdownMenuItem onClick={() => navigate('/buyer/dashboard')}>
              Buyer Dashboard
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/seller/dashboard')}>
              Seller Dashboard
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-destructive">
              <LogOut className="h-4 w-4 mr-2" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
